<!-- Loads an image thumbnail using the specified image_id.  
        The image uses lazy loading by default but you can change it 
-->
<script lang="ts">
    import { defineComponent } from "vue"
    import * as fileUtils from '../lib/FileUtils'

    import { ChoiceApi } from "../LibMain";
    
    export default defineComponent({
        name: "ChoiceThumbnail",

        data() {
            return {
                imageUrl: "",
                imageName: "",
                imgId: null,    // Get after we mount the control
                observer: {} as IntersectionObserver,
                releaseImageTimer: undefined as NodeJS.Timeout | undefined,
                fetchingImage: false
            };
        },

        props:{
            // The ID for the Choice/OfficeMD image
            image_id: { type: Number, required: true},

            // Do we want to use lazy loading to avoid downloading the image until it would be visible
            lazyLoad: { type: Boolean, default: true },

            // If the image is no longer visible -- is only valid if lazyLoad is true
            releaseLazyLoadTimeoutMsec: {type: Number, default: 10*1000 }  
        },

        mounted(){
            if (this.lazyLoad)
                this.initializeObserver();
            else
                this.fetchThumbnailFromApi();
        },

        methods: {
            initializeObserver() {
                const imgEle = this.$refs.imgThumbnail as any;
                if (!imgEle)
                {
                    console.log("imgEle in ChoiceThumbnail should not be null...");
                }
                this.imgId = imgEle.id;

                const loadOptions = {
                        root: null,
                        rootMargin: '20%',
                        //threshold: 0.50
                    };
    
                this.observer = new IntersectionObserver((entries, _observer) => this.manageLazyLoad(entries), loadOptions);
                this.observer.observe(imgEle);
            },

            manageLazyLoad(intersectionObserverEntries?: Array<IntersectionObserverEntry>) {

                const thisEleEntry = intersectionObserverEntries?.find(e => e.target.id == this.imgId);
                if (!this.imageUrl)
                {
                    if (true === thisEleEntry?.isIntersecting)
                        this.fetchThumbnailFromApi();
                }
                else
                {
                    // Is the image no longer visible?
                    if (false === thisEleEntry?.isIntersecting)
                    {
                        // Release the image data at a specified time from now
                        //  Don't allow for less than 5 seconds to unload.  Don't get over eager!
                        if (!this.releaseImageTimer && this.lazyLoad && this.releaseLazyLoadTimeoutMsec > 0)
                            this.releaseImageTimer = setTimeout(this.releaseImage, Math.max(5000, this.releaseLazyLoadTimeoutMsec));
                    }
                    else
                    {
                        if (this.releaseImageTimer)
                            clearTimeout(this.releaseImageTimer);
                    }
                }
            },

            fetchThumbnailFromApi(){
                if (this.fetchingImage)
                    return;

                this.fetchingImage = true;
                console.log(`Fetching ${this.image_id}`);

                ChoiceApi.documentManager.thumbnail(this.image_id, this.logError).then(resp => {
                    if (resp)
                    {                        
                        this.imageUrl = fileUtils.base64AndMimeTypeToUrl(resp.base64, resp.mimeType);
                        this.imageName = resp.fileName;
                    }
                })
                .catch(e => {
                    console.log("Exception loading image: " + e?.message);
                })
                .finally(() => {
                    this.fetchingImage = false;
                });

            },

            releaseImage()
            {
                if (this.lazyLoad)
                {
                    this.imageUrl = "";
                    console.log("Uloading " + this.image_id);
                }
            },

            async logError(httpResp: Response){
                const respText = await httpResp.text();
                console.log(`Error loading image_id ${this.image_id}: ${respText}`);
            }
        }
    });

</script>

<template>
    <img ref="imgThumbnail" :src="imageUrl" :title="imageName" :alt="imageName" />
</template>